import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Users, Download, ShoppingCart, AlertTriangle, Gift, Percent, Trash2, Plus, TrendingUp, TrendingDown, CreditCard, DollarSign, Crown, UserMinus, Package } from 'lucide-react';
import { SmartbouffeLogoLarge } from '../components/Logo';
import { Footer } from '../components/Footer';

export default function Admin() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [showSyncSubscription, setShowSyncSubscription] = useState(false);
  const [syncPaypalId, setSyncPaypalId] = useState('');
  const [syncUserEmail, setSyncUserEmail] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    promo_type: 'credits',
    value: 10,
    max_uses: null,
    expires_at: ''
  });

  useEffect(() => {
    fetchDashboard();
    fetchPromoCodes();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
      setDashboard(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Accès admin requis");
        navigate('/dashboard');
      } else {
        toast.error("Erreur lors du chargement");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/promo-codes`, { withCredentials: true });
      setPromoCodes(response.data.promo_codes || []);
    } catch (error) {
      setPromoCodes([]);
    }
  };

  const createPromoCode = async () => {
    if (!newPromo.code.trim()) {
      toast.error("Le code est requis");
      return;
    }

    try {
      await axios.post(`${API}/admin/promo-codes`, {
        code: newPromo.code.toUpperCase(),
        promo_type: newPromo.promo_type,
        value: parseInt(newPromo.value),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        expires_at: newPromo.expires_at || null
      }, { withCredentials: true });

      toast.success("Code promo créé!");
      setShowCreatePromo(false);
      setNewPromo({ code: '', promo_type: 'credits', value: 10, max_uses: null, expires_at: '' });
      fetchPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(`Supprimer le code ${code}?`)) return;

    try {
      await axios.delete(`${API}/admin/promo-codes/${code}`, { withCredentials: true });
      toast.success("Code promo supprimé");
      fetchPromoCodes();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const syncSubscription = async () => {
    if (!syncPaypalId.trim()) {
      toast.error("L'ID d'abonnement PayPal est requis");
      return;
    }
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/admin/sync-subscription`, {
        paypal_subscription_id: syncPaypalId.trim(),
        user_email: syncUserEmail.trim() || null
      }, { withCredentials: true });
      
      toast.success(`✅ ${response.data.message}\n${response.data.user_email} → ${response.data.plan_name} (${response.data.downloads_per_month} téléchargements/mois)`);
      setShowSyncSubscription(false);
      setSyncPaypalId('');
      setSyncUserEmail('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la synchronisation");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #F5F1E8 0%, #E8E4D9 100%)'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #F5F1E8 0%, #E8E4D9 100%)', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        padding: '1rem 1.5rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        marginBottom: '2rem'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <Button onClick={() => navigate('/dashboard')} variant="ghost" style={{ padding: '0.5rem' }}>
            <ArrowLeft size={24} />
          </Button>
          <SmartbouffeLogoLarge style={{ width: '120px' }} />
          <span style={{ 
            marginLeft: 'auto', 
            background: '#87A96B', 
            color: 'white', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '20px',
            fontSize: '0.85rem',
            fontWeight: '600'
          }}>
            Admin
          </span>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        {/* Title */}
        <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E', marginBottom: '2rem' }}>
          Dashboard Administrateur
        </h1>

        {/* KPI Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {/* Users */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Users size={24} color="#87A96B" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Membres</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.total_users || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#87A96B' }}>+{kpis.new_users_30d || 0} ce mois</div>
          </div>

          {/* Active Users */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={24} color="#4CAF50" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Actifs (30j)</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.active_users_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#DC3545' }}>{kpis.inactive_users_30d || 0} inactifs</div>
          </div>

          {/* Extractions */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Download size={24} color="#2196F3" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Extractions</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.total_recipes || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#87A96B' }}>+{kpis.successful_extractions_30d || 0} ce mois</div>
          </div>

          {/* Success Rate */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <AlertTriangle size={24} color="#FFA726" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Taux succès</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: kpis.extraction_success_rate >= 80 ? '#4CAF50' : '#FFA726' }}>
              {kpis.extraction_success_rate || 0}%
            </div>
            <div style={{ fontSize: '0.85rem', color: '#DC3545' }}>{kpis.extraction_errors_30d || 0} erreurs</div>
          </div>

          {/* Revenue */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <ShoppingCart size={24} color="#9C27B0" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Revenus (30j)</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>${kpis.total_revenue_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#87A96B' }}>{kpis.total_purchases_30d || 0} achats</div>
          </div>

          {/* Total Buyers */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Users size={24} color="#FF5722" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Acheteurs</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.total_buyers || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>total</div>
          </div>

          {/* Promo Codes Used */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Gift size={24} color="#E91E63" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Codes promo</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.promo_codes_used_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#87A96B' }}>{kpis.active_promo_codes || 0} actifs</div>
          </div>

          {/* Account Deletions */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <TrendingDown size={24} color="#DC3545" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Suppressions</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.account_deletions_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>ce mois</div>
          </div>
        </div>

        {/* Section Abonnements */}
        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3A4A3E', marginBottom: '1rem', marginTop: '2rem' }}>
          📊 Statistiques Abonnements
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {/* MRR */}
          <div style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <DollarSign size={24} color="white" />
              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>MRR</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'white' }}>${kpis.mrr || 0}</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>Revenus récurrents mensuels</div>
          </div>

          {/* Total Revenus Mensuels */}
          <div style={{ background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <CreditCard size={24} color="white" />
              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>Revenus totaux</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'white' }}>${kpis.total_monthly_revenue || 0}</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>MRR + achats ponctuels</div>
          </div>

          {/* Abonnés payants */}
          <div style={{ background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Crown size={24} color="white" />
              <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.9rem' }}>Abonnés payants</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: 'white' }}>{kpis.total_paying_subscribers || 0}</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)' }}>tous forfaits confondus</div>
          </div>

          {/* Nouvelles souscriptions */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={24} color="#4CAF50" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Nouvelles souscriptions</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#4CAF50' }}>+{kpis.new_subscriptions_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>ce mois</div>
          </div>

          {/* Annulations */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <UserMinus size={24} color="#DC3545" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Annulations</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#DC3545' }}>{kpis.cancelled_subscriptions_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>ce mois</div>
          </div>

          {/* Achats vidéos recettes bonus */}
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <Package size={24} color="#2196F3" />
              <span style={{ color: '#8B7355', fontSize: '0.9rem' }}>Vidéos recettes bonus</span>
            </div>
            <div style={{ fontSize: '2rem', fontWeight: '700', color: '#3A4A3E' }}>{kpis.extra_downloads_purchases_30d || 0}</div>
            <div style={{ fontSize: '0.85rem', color: '#87A96B' }}>${kpis.extra_downloads_revenue_30d || 0} de revenus</div>
          </div>
        </div>

        {/* Répartition des abonnés par forfait */}
        {dashboard?.subscribers_by_plan && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#3A4A3E', marginBottom: '1rem' }}>
              👥 Répartition des abonnés par forfait
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
              {Object.entries(dashboard.subscribers_by_plan).map(([planId, count]) => {
                const planNames = {
                  free: 'Gratuit',
                  starter: 'Starter',
                  standard: 'Standard',
                  pro: 'Pro',
                  unlimited: 'Illimité'
                };
                const planColors = {
                  free: '#9E9E9E',
                  starter: '#4CAF50',
                  standard: '#2196F3',
                  pro: '#FF9800',
                  unlimited: '#9C27B0'
                };
                const planPrice = dashboard.plan_prices?.[planId] || 0;
                
                return (
                  <div 
                    key={planId} 
                    style={{ 
                      textAlign: 'center', 
                      padding: '1rem',
                      background: `${planColors[planId]}15`,
                      borderRadius: '8px',
                      border: `2px solid ${planColors[planId]}30`
                    }}
                  >
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: planColors[planId] }}>{count}</div>
                    <div style={{ fontSize: '0.95rem', fontWeight: '600', color: '#3A4A3E' }}>{planNames[planId]}</div>
                    {planPrice > 0 && (
                      <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>${planPrice}/mois</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Bundle Sales */}
        {dashboard?.bundle_sales && Object.keys(dashboard.bundle_sales).length > 0 && (
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#3A4A3E', marginBottom: '1rem' }}>
              Forfaits vendus (30 derniers jours)
            </h2>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              {Object.entries(dashboard.bundle_sales).map(([bundleId, count]) => (
                <div key={bundleId} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#87A96B' }}>{count}</div>
                  <div style={{ fontSize: '0.85rem', color: '#8B7355' }}>{bundleId.replace('bundle_', '')} crédits</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Promo Codes Section */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#3A4A3E' }}>
              Codes Promo
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button onClick={() => setShowSyncSubscription(true)} variant="outline" style={{ borderColor: '#FFC439', color: '#8B7355' }}>
                <CreditCard size={18} style={{ marginRight: '0.5rem' }} />
                Sync PayPal
              </Button>
              <Button onClick={() => setShowCreatePromo(true)} style={{ background: '#87A96B' }}>
                <Plus size={18} style={{ marginRight: '0.5rem' }} />
                Créer un code
              </Button>
            </div>
          </div>

          {promoCodes.length === 0 ? (
            <p style={{ color: '#8B7355', textAlign: 'center', padding: '2rem' }}>
              Aucun code promo actif
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #E8E4D9' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#5D6F61' }}>Code</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#5D6F61' }}>Type</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#5D6F61' }}>Valeur</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#5D6F61' }}>Utilisations</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem', color: '#5D6F61' }}>Expiration</th>
                    <th style={{ textAlign: 'right', padding: '0.75rem', color: '#5D6F61' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((promo) => (
                    <tr key={promo.id} style={{ borderBottom: '1px solid #F5F1E8' }}>
                      <td style={{ padding: '0.75rem', fontWeight: '600', color: '#3A4A3E' }}>{promo.code}</td>
                      <td style={{ padding: '0.75rem' }}>
                        <span style={{
                          background: promo.promo_type === 'credits' ? '#E3F2FD' : '#FFF3E0',
                          color: promo.promo_type === 'credits' ? '#1976D2' : '#E65100',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          {promo.promo_type === 'credits' ? 'Crédits' : 'Rabais'}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', color: '#3A4A3E' }}>
                        {promo.promo_type === 'credits' ? `${promo.value} crédits` : `${promo.value}%`}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#3A4A3E' }}>
                        {promo.current_uses || 0}{promo.max_uses ? ` / ${promo.max_uses}` : ' (illimité)'}
                      </td>
                      <td style={{ padding: '0.75rem', color: '#8B7355' }}>
                        {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('fr-CA') : 'Jamais'}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePromoCode(promo.code)}
                          style={{ color: '#DC3545' }}
                        >
                          <Trash2 size={18} />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Promo Code Dialog */}
      <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Créer un code promo</DialogTitle>
          </DialogHeader>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1rem 0' }}>
            <div>
              <Label>Code (sera en majuscules)</Label>
              <Input
                value={newPromo.code}
                onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                placeholder="ex: FAMILLE10"
              />
            </div>

            <div>
              <Label>Type</Label>
              <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem' }}>
                <button
                  onClick={() => setNewPromo({ ...newPromo, promo_type: 'credits' })}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: `2px solid ${newPromo.promo_type === 'credits' ? '#87A96B' : '#E8E4D9'}`,
                    borderRadius: '8px',
                    background: newPromo.promo_type === 'credits' ? '#F0F7ED' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Gift size={18} />
                  Crédits gratuits
                </button>
                <button
                  onClick={() => setNewPromo({ ...newPromo, promo_type: 'discount' })}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    border: `2px solid ${newPromo.promo_type === 'discount' ? '#87A96B' : '#E8E4D9'}`,
                    borderRadius: '8px',
                    background: newPromo.promo_type === 'discount' ? '#F0F7ED' : 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Percent size={18} />
                  Rabais %
                </button>
              </div>
            </div>

            <div>
              <Label>
                {newPromo.promo_type === 'credits' ? 'Nombre de crédits' : 'Pourcentage de rabais'}
              </Label>
              <Input
                type="number"
                value={newPromo.value}
                onChange={(e) => setNewPromo({ ...newPromo, value: e.target.value })}
                min={1}
                max={newPromo.promo_type === 'discount' ? 100 : 1000}
              />
            </div>

            <div>
              <Label>Limite d'utilisation (optionnel)</Label>
              <Input
                type="number"
                value={newPromo.max_uses || ''}
                onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value || null })}
                placeholder="Illimité si vide"
                min={1}
              />
            </div>

            <div>
              <Label>Date d'expiration (optionnel)</Label>
              <Input
                type="date"
                value={newPromo.expires_at}
                onChange={(e) => setNewPromo({ ...newPromo, expires_at: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePromo(false)}>
              Annuler
            </Button>
            <Button onClick={createPromoCode} style={{ background: '#87A96B' }}>
              Créer le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Sync Subscription */}
      <Dialog open={showSyncSubscription} onOpenChange={setShowSyncSubscription}>
        <DialogContent style={{ maxWidth: '450px' }}>
          <DialogHeader>
            <DialogTitle>Synchroniser un abonnement PayPal</DialogTitle>
          </DialogHeader>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              Si un abonnement PayPal n'a pas été activé automatiquement, entrez l'ID d'abonnement pour le synchroniser.
            </p>
            
            <div>
              <Label>ID d'abonnement PayPal *</Label>
              <Input
                placeholder="I-XXXXXXXXXX"
                value={syncPaypalId}
                onChange={(e) => setSyncPaypalId(e.target.value)}
              />
              <p style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.25rem' }}>
                Trouvez-le dans PayPal Business → Abonnements
              </p>
            </div>
            
            <div>
              <Label>Email de l'utilisateur (optionnel)</Label>
              <Input
                type="email"
                placeholder="Laissez vide pour utiliser l'email PayPal"
                value={syncUserEmail}
                onChange={(e) => setSyncUserEmail(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter style={{ marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowSyncSubscription(false)}>
              Annuler
            </Button>
            <Button 
              onClick={syncSubscription} 
              disabled={syncing}
              style={{ background: '#FFC439', color: '#2C2E2F' }}
            >
              {syncing ? 'Synchronisation...' : 'Synchroniser'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
